// The health dot (spec 15 §4 S4, S5, S15; D-612): one chip naming the
// tenant's state in words, never a bare colour, with the reasons the server
// gave. The reasons are text inside the chip's wrapper, visually hidden, so a
// screen reader, a keyboard user and a touch user's assistive tech all reach
// them; the title repeats them for a mouse hover.

import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../components/status-chip";
import { dotVariant } from "../lib/health";
import type { HealthSummary } from "../lib/platform-api";

export function HealthDot({
  health,
  "data-testid": testId = "admin-health-dot",
}: {
  health: HealthSummary;
  "data-testid"?: string;
}) {
  const { t } = useTranslation();
  const reasons = health.reasons.map((r) => t(`admin.health.reasons.${r}`)).join(" · ");
  return (
    <span title={reasons === "" ? undefined : reasons} data-dot={health.dot} data-testid={testId}>
      <StatusChip variant={dotVariant(health.dot)}>
        {t(`admin.health.dot.${health.dot}`)}
      </StatusChip>
      {reasons === "" ? null : (
        <span className="visually-hidden" data-testid={`${testId}-reasons`}>
          {`: ${reasons}`}
        </span>
      )}
    </span>
  );
}

/** A row of window cells: one per window or day, coloured by tone, with the
 * count of each tone as the row's accessible name. Background only, no text,
 * so the cells carry no contrast pair of their own. */
export function HealthStrip({
  label,
  cells,
  summary,
  "data-testid": testId,
}: {
  label: string;
  cells: readonly { key: string; tone: "good" | "warn" | "bad" | "none"; title: string }[];
  summary: string;
  "data-testid"?: string;
}) {
  return (
    <div className="admin-strip-row" data-testid={testId}>
      <span className="admin-strip-label">{label}</span>
      <span className="admin-strip" role="img" aria-label={`${label}: ${summary}`}>
        {cells.map((c) => (
          <span
            key={c.key}
            className={`admin-strip-cell admin-strip-${c.tone}`}
            title={c.title}
            data-tone={c.tone}
          />
        ))}
      </span>
      <span className="admin-strip-sum">{summary}</span>
    </div>
  );
}
